import Vue from 'vue';
import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import MessageApi from '../../api/inbox/message';

const state = {
  records: {},
  showConversationModal: false,
  contactConversationData: null,
  meta: {
    count: 0,
    currentPage: 1,
  },
  uiFlags: {
    isFetching: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getShowConversationModal($state) {
    return $state.showConversationModal;
  },
  getContactConversationData($state) {
    return $state.contactConversationData;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getMeta: $state => {
    return $state.meta;
  },
};

export const actions = {
  create: async ({ commit }, params) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const {
      inboxId,
      message,
      contactId,
      sourceId,
      mailSubject,
      assigneeId,
      receiver,
      conversationId,
      forwardedMessageId,
    } = params;
    try {
      const { data } = await ConversationApi.create({
        inbox_id: inboxId,
        source_id: sourceId,
        additional_attributes: {
          mail_subject: mailSubject,
        },
        ...((!message.files || message.files.length === 0) && { message }),
        ...(receiver
          ? {
              receiver,
              conversation_id: conversationId,
              forwarded_message_id: forwardedMessageId,
            }
          : { contact_id: contactId }),
        assignee_id: assigneeId,
      });
      if (message?.files?.length > 0) {
        await MessageApi.create({
          conversationId: data.id,
          private: false,
          message: message.content,
          ...message,
          ...(receiver && { forwardedMessageId }),
        });
      }
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: contactId,
        data,
      });
      return data;
    } catch (error) {
      if (error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  createEmail: async ({ commit }, params) => {
    commit(types.default.SET_EMAIL_CONVERSATIONS_UI_FLAG, {
      isCreatingEmail: true,
    });
    const { inboxId, message, receivers, mailSubject, assigneeId } = params;
    try {
      const { data } = await ConversationApi.createEmail({
        inbox_id: inboxId,
        receivers: receivers,
        additional_attributes: {
          mail_subject: mailSubject,
        },
        ...((!message.files || message.files.length === 0) && { message }),
        assignee_id: assigneeId,
      });
      if (message?.files?.length > 0) {
        const ids = data.map(item => item.id).join(',');
        await MessageApi.createEmails({
          conversations: ids,
          private: false,
          message: message.content,
          ...message,
        });
      }
      return data;
    } catch (error) {
      if (error.response.data.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    } finally {
      commit(types.default.SET_EMAIL_CONVERSATIONS_UI_FLAG, {
        isCreatingEmail: false,
      });
    }
  },
  get: async ({ commit }, { contactId, page }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const { data } = await ContactAPI.getConversations(contactId, page);
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: data.payload,
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_META, data.meta);

      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  setContactConversationsCallStatus({ commit }, data) {
    commit(types.default.SET_CONTACT_CONVERSATIONS_CALL_STATUS, data);
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_EMAIL_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS_META]: ($state, data) => {
    const { items_count: count, current_page: currentPage } = data;
    Vue.set($state, 'meta', { count, currentPage });
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    Vue.set($state.records, id, data);
  },
  [types.default.SET_CONTACT_CONVERSATION_VISIBILITY]: ($state, show) => {
    $state.showConversationModal = show;
  },
  [types.default.SET_CONTACT_CONVERSATION_DATA]: ($state, data) => {
    $state.contactConversationData = data;
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    Vue.set($state.records, id, updatedConversations);
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    Vue.delete($state.records, id);
  },
  [types.default.SET_CONTACT_CONVERSATIONS_CALL_STATUS]: ($state, id) => {
    Vue.delete($state.records, id);
  },
  [types.default.SET_CONTACT_CONVERSATIONS_CALL_STATUS]: ($state, data) => {
    Object.values($state.records).forEach(array => {
      array.forEach(conversation => {
        if (
          data.conversationId === 'all' ||
          conversation.id === data.conversationId
        ) {
          conversation.call_status = data.status;
        }
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
